import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  AutoMode as AutoModeIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const WorkflowAutomationFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Streamlined metadata curation and approval processes',
    'Reduced manual effort and human error',
    'Faster time-to-value for data initiatives',
    'Consistent application of data quality standards',
    'Automated notifications and escalations',
    'Improved collaboration between data teams'
  ];

  const capabilities = [
    {
      icon: <AutoModeIcon sx={{ color: '#003366' }} />,
      title: 'Process Automation',
      description: 'Automate repetitive metadata management tasks and workflows'
    },
    {
      icon: <SpeedIcon sx={{ color: '#003366' }} />,
      title: 'Rapid Approvals',
      description: 'Accelerate approval cycles with automated routing and notifications'
    },
    {
      icon: <AssignmentIcon sx={{ color: '#003366' }} />,
      title: 'Task Management',
      description: 'Assign, track, and manage data governance tasks across teams'
    },
    {
      icon: <NotificationsIcon sx={{ color: '#003366' }} />,
      title: 'Smart Alerts',
      description: 'Automated notifications for workflow events and exceptions'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: '#003366' }}
      >
        Back
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SwapHorizIcon sx={{ fontSize: 48, color: '#003366', mr: 2 }} />
          <Typography variant="h3" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Workflow Automation
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Streamlined processes for metadata curation, approvals, and change management
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/tasks')}
          sx={{
            bgcolor: '#003366',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#002244'
            }
          }}
        >
          Access Workflow & Tasks Application →
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          Workflow Automation streamlines data governance processes by automating repetitive tasks, approval workflows, and change management procedures. This reduces manual effort, accelerates decision-making, and ensures consistent application of data quality standards.
        </Typography>
        <Typography paragraph>
          The framework supports configurable workflows that can be tailored to your organization's specific needs, from simple approval chains to complex multi-step processes involving multiple stakeholders across DHS components.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#003366' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: '#003366', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Metadata Approval
          </Typography>
          <Typography paragraph>
            Automate the review and approval process for new or updated metadata definitions.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Change Management
          </Typography>
          <Typography paragraph>
            Manage data changes through structured workflows with proper approvals and documentation.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Quality Assurance
          </Typography>
          <Typography paragraph>
            Automate data quality checks and route exceptions to appropriate teams for resolution.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 4, bgcolor: '#003366', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Automate Your Data Workflows?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Workflow Automation can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: '#003366',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default WorkflowAutomationFeature;
